// CRM Intelligence — three-column cockpit (25/50/25).
// Left: composite-ranked company rail (search, filters, pagination + page size).
// Centre: AI intelligence analyst chat by default; swaps to the full company
//         intelligence surface when a company is selected. The chat is the
//         platform-home analyst — ask it what to act on, it answers from live data.
// Right: cross-company activity feed.
import { useState, useEffect, useCallback, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import API_BASE from '../../config';
import raptorIcon from '../../assets/raptor.svg';
import raptorMark from '../../assets/raptor-white.svg';
import CrmCompanyIntelligence from './CrmCompanyIntelligence';

const SIGNAL_COLOURS = {
  funding: { c: 'var(--crm-green)', bg: 'var(--crm-green-bg)', l: 'Funding' },
  leadership_change: { c: 'var(--crm-violet)', bg: 'var(--crm-violet-bg)', l: 'Leadership' },
  hiring: { c: 'var(--crm-blue)', bg: 'var(--crm-blue-bg)', l: 'Hiring' },
  tech_change: { c: 'var(--crm-cyan)', bg: 'var(--crm-cyan-bg)', l: 'Tech' },
  news: { c: 'var(--crm-slate)', bg: 'var(--crm-slate-bg)', l: 'News' }
};
const FEED_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'funding', label: 'Funding' },
  { key: 'leadership_change', label: 'Leadership' },
  { key: 'hiring', label: 'Hiring' },
  { key: 'tech_change', label: 'Tech' }
];
const STARTERS = [
  'What should I prioritise today and why?',
  'Which companies have fresh signals this week?',
  'Which digests are ready to send?',
  'Summarise the most important signal right now'
];
const PAGE_SIZES = [25, 50, 100];

function scoreBand(s) {
  const n = Number(s) || 0;
  if (n >= 70) return { label: 'Hot', c: 'var(--crm-green)', bg: 'var(--crm-green-bg)' };
  if (n >= 40) return { label: 'Warm', c: 'var(--crm-amber)', bg: 'var(--crm-amber-bg)' };
  return { label: 'Cold', c: 'var(--crm-slate)', bg: 'var(--crm-slate-bg)' };
}
function tierChip(c) {
  if (c.rank_tier === 1) { const n = Number(c.fresh_signal_count) || 0; return { label: n + ' fresh signal' + (n === 1 ? '' : 's'), c: 'var(--crm-blue)', bg: 'var(--crm-blue-bg)' }; }
  if (c.rank_tier === 2) return { label: 'Digest ready', c: 'var(--crm-violet)', bg: 'var(--crm-violet-bg)' };
  if (c.rank_tier === 3) return { label: 'Due for sweep', c: 'var(--crm-amber)', bg: 'var(--crm-amber-bg)' };
  return { label: 'Up to date', c: 'var(--crm-faint)', bg: 'var(--crm-line-soft)' };
}
function relTime(iso) {
  if (!iso) return 'never';
  const d = new Date(iso), mins = Math.floor((Date.now() - d) / 60000);
  if (mins < 60) return Math.max(mins, 0) + 'm';
  const h = Math.floor(mins / 60); if (h < 24) return h + 'h';
  const days = Math.floor(h / 24); if (days < 30) return days + 'd';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}
function Favicon({ website, name, size = 30 }) {
  const [failed, setFailed] = useState(false);
  let domain = '';
  try { domain = website ? new URL(website.startsWith('http') ? website : 'https://' + website).hostname : ''; } catch (_) {}
  const initials = (name || '?').split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  if (!domain || failed) return <div className="crmiq-fav crmiq-fav--fallback" style={{ width: size, height: size }}>{initials}</div>;
  return <img className="crmiq-fav" style={{ width: size, height: size }} alt="" src={`https://www.google.com/s2/favicons?domain=${domain}&sz=64`} onError={() => setFailed(true)} />;
}

// Render one line of inline markdown: **bold** and [#id] chips.
function renderInline(line, refMap, onPick, keyBase) {
  const tokens = line.split(/(\[#\d+\]|\*\*[^*]+\*\*)/g).filter(Boolean);
  return tokens.map((t, i) => {
    const chip = t.match(/^\[#(\d+)\]$/);
    if (chip) {
      const cid = parseInt(chip[1], 10);
      return <button key={keyBase + '-' + i} className="crmiq-refchip" onClick={() => onPick(cid)}>{refMap[cid] || ('#' + cid)}</button>;
    }
    const bold = t.match(/^\*\*([^*]+)\*\*$/);
    if (bold) return <strong key={keyBase + '-' + i}>{bold[1]}</strong>;
    return <span key={keyBase + '-' + i}>{t}</span>;
  });
}

function ChatAnswer({ text, refMap, onPick }) {
  const lines = String(text).replace(/\n{3,}/g, '\n\n').split('\n');
  const blocks = [];
  let list = null;
  lines.forEach((raw, i) => {
    const line = raw.trimEnd();
    const heading = line.match(/^#{1,4}\s+(.*)/);
    const bullet = line.match(/^\s*[-*]\s+(.*)/);
    const numbered = line.match(/^\s*(\d+)[.)]\s+(.*)/);
    if (bullet || numbered) {
      const content = bullet ? bullet[1] : numbered[2];
      if (!list) { list = []; }
      list.push(<li key={'li' + i} className="crmiq-ans-li">{renderInline(content, refMap, onPick, 'li' + i)}</li>);
      return;
    }
    if (list) { blocks.push(<ul key={'ul' + i} className="crmiq-ans-ul">{list}</ul>); list = null; }
    if (heading) { blocks.push(<div key={'h' + i} className="crmiq-ans-h">{renderInline(heading[1], refMap, onPick, 'h' + i)}</div>); return; }
    if (line === '') { blocks.push(<div key={'sp' + i} className="crmiq-ans-sp" />); return; }
    blocks.push(<p key={'p' + i} className="crmiq-ans-p">{renderInline(line, refMap, onPick, 'p' + i)}</p>);
  });
  if (list) blocks.push(<ul key="ul-end" className="crmiq-ans-ul">{list}</ul>);
  return <div className="crmiq-ans">{blocks}</div>;
}

export default function CrmIntelligence() {
  const { clientId, client } = useOutletContext();
  const { getToken } = useAuth();

  const [companies, setCompanies] = useState([]);
  const [summary, setSummary] = useState({ total: 0, fresh_signals: 0, unsent_digests: 0, due: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selected, setSelected] = useState(null);
  const [selectedTab, setSelectedTab] = useState('overview');
  const [bulk, setBulk] = useState(new Set());
  const [sweeping, setSweeping] = useState(false);

  const [feed, setFeed] = useState([]);
  const [feedFilter, setFeedFilter] = useState('all');

  const [chat, setChat] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatBusy, setChatBusy] = useState(false);
  const chatEndRef = useRef(null);

  const load = useCallback(async () => {
    if (!clientId) return;
    try {
      const r = await fetch(`${API_BASE}/clients/${clientId}/intelligence/queue`);
      if (r.ok) { const d = await r.json(); setCompanies(d.companies || []); setSummary(d.summary || summary); }
    } catch (_) {} finally { setLoading(false); }
  }, [clientId]); // eslint-disable-line

  const loadFeed = useCallback(async () => {
    if (!clientId) return;
    try {
      const q = feedFilter === 'all' ? '' : `&signal_type=${feedFilter}`;
      const r = await fetch(`${API_BASE}/clients/${clientId}/intelligence/feed?limit=60${q}`);
      if (r.ok) { const d = await r.json(); setFeed(d.feed || []); }
    } catch (_) {}
  }, [clientId, feedFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadFeed(); }, [loadFeed]);
  useEffect(() => { setPage(1); }, [filter, search, pageSize]);
  useEffect(() => { if (chat.length > 0 || chatBusy) chatEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); }, [chat, chatBusy]);

  async function runSweep(ids, prompt) {
    if (!ids.length) return;
    setSweeping(true);
    try {
      const token = await getToken();
      await fetch(`${API_BASE}/clients/${clientId}/intelligence/sweep`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ company_ids: ids.map(Number), ...(prompt ? { prompt } : {}) })
      });
    } catch (_) {} finally { setSweeping(false); setBulk(new Set()); load(); loadFeed(); }
  }

  async function sendChat(text) {
    const msg = (text != null ? text : chatInput).trim();
    if (!msg || chatBusy) return;
    const viewing = selected != null ? companies.find(c => c.id === selected) : null;
    const sent = viewing ? `(I am currently viewing ${viewing.name} [#${viewing.id}].) ${msg}` : msg;
    const history = chat.map(m => ({ role: m.role, content: m.content }));
    setChat(c => [...c, { role: 'user', content: msg }]);
    setChatInput('');
    setSelected(null); // return to the analyst thread so the answer is visible
    setChatBusy(true);
    try {
      const r = await fetch(`${API_BASE}/clients/${clientId}/intelligence/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: sent, history })
      });
      const d = await r.json();
      if (r.ok) setChat(c => [...c, { role: 'assistant', content: d.answer || '', referenced: d.referenced || [] }]);
      else setChat(c => [...c, { role: 'assistant', content: 'Sorry, that failed: ' + (d.error || 'unknown error'), referenced: [] }]);
    } catch (e) {
      setChat(c => [...c, { role: 'assistant', content: 'Network error. Try again.', referenced: [] }]);
    } finally { setChatBusy(false); }
  }

  function toggleBulk(id) { setBulk(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; }); }

  const filtered = companies.filter(c => {
    if (search && !c.name?.toLowerCase().includes(search.toLowerCase())) return false;
    if (filter === 'signals' && c.rank_tier !== 1) return false;
    if (filter === 'digests' && c.rank_tier !== 2) return false;
    if (filter === 'due' && c.rank_tier !== 3) return false;
    return true;
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageItems = filtered.slice((page - 1) * pageSize, page * pageSize);

  const feedItems = feed.filter(f => feedFilter === 'all' || f.signal_type === feedFilter);
  const refMap = {};
  chat.forEach(m => (m.referenced || []).forEach(r => { refMap[r.id] = r.name; }));

  const pickCompany = (id, tab = 'overview') => { setSelectedTab(tab); setSelected(id); };

  return (
    <div className="crmiq3">
      <div className="crmiq3-rail">
        <div className="crmiq3-rail-head">
          <input className="crmiq-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search companies…" />
          <div className="crmiq3-filters">
            {[
              { k: 'all', l: 'All', n: summary.total },
              { k: 'signals', l: 'Signals', n: summary.fresh_signals },
              { k: 'digests', l: 'Digests', n: summary.unsent_digests },
              { k: 'due', l: 'Due', n: summary.due }
            ].map(f => (
              <button key={f.k} className={'crmiq3-fpill' + (filter === f.k ? ' crmiq3-fpill--on' : '')} onClick={() => setFilter(f.k)}>
                {f.l}{f.n > 0 && <span className="crmiq3-fpill-n">{f.n}</span>}
              </button>
            ))}
          </div>
        </div>

        <div className="crmiq3-list">
          {loading ? <p className="crmiq-empty">Loading…</p>
            : pageItems.length === 0 ? <p className="crmiq-empty">No companies match.</p>
            : pageItems.map(c => {
              const band = scoreBand(c.score), chip = tierChip(c), isSel = selected === c.id;
              return (
                <div key={c.id} className={'crmiq3-row' + (isSel ? ' crmiq3-row--on' : '')} onClick={() => pickCompany(c.id)}>
                  <span className="crmiq-band" style={{ background: band.c }} />
                  <input type="checkbox" className="crmiq-check" checked={bulk.has(c.id)} onChange={() => toggleBulk(c.id)} onClick={e => e.stopPropagation()} />
                  <Favicon website={c.website} name={c.name} size={26} />
                  <div className="crmiq3-row-main">
                    <div className="crmiq3-row-name">{c.name}</div>
                    <div className="crmiq3-row-meta">
                      <span className="crmiq-chip" style={{ color: chip.c, background: chip.bg }}>{chip.label}</span>
                      <span className="crmiq-meta">{relTime(c.last_swept_at)}</span>
                    </div>
                  </div>
                  <span className="crmiq3-dot" style={{ background: band.c }} title={band.label + ' · ' + (c.score || 0)} />
                </div>
              );
            })}
        </div>

        <div className="crmiq3-pager">
          <select className="crmiq3-psize" value={pageSize} onChange={e => setPageSize(Number(e.target.value))}>
            {PAGE_SIZES.map(s => <option key={s} value={s}>{s} / page</option>)}
          </select>
          <div className="crmiq3-pnav">
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹</button>
            <span>{page} / {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>›</button>
          </div>
        </div>

        {bulk.size > 0 && (
          <div className="crmiq3-bulk">
            <span>{bulk.size} selected</span>
            <button className="crmiq-btn crmiq-btn--primary" disabled={sweeping} onClick={() => runSweep(Array.from(bulk))}>{sweeping ? 'Sweeping…' : 'Sweep'}</button>
            <button className="crmiq-btn crmiq-btn--ghost" onClick={() => setBulk(new Set())}>Clear</button>
          </div>
        )}
      </div>

      <div className="crmiq3-centre">
        <div className="crmiq3-centre-top">
          {selected == null ? (
            <div className="crmiq3-chat-body">
              {chat.length === 0 && !chatBusy && (
                <div className="crmiq3-chat-welcome">
                  <div className="crmiq3-welcome-mark"><img src={raptorMark} alt="Raptor" /></div>
                  <p className="crmiq3-welcome-lead">What do you want to know about {client?.name || 'this client'}?</p>
                  <p className="crmiq3-welcome-sub">{summary.fresh_signals} fresh signals · {summary.unsent_digests} digests ready · {summary.due} due for sweep</p>
                  <div className="crmiq3-starters">
                    {STARTERS.map(s => <button key={s} className="crmiq3-starter" onClick={() => sendChat(s)}>{s}</button>)}
                  </div>
                </div>
              )}
              {chat.map((m, i) => (
                <div key={i} className={'crmiq3-msg crmiq3-msg--' + m.role}>
                  {m.role === 'assistant' && <div className="crmiq3-msg-mark">CE</div>}
                  <div className="crmiq3-msg-body">
                    {m.role === 'assistant'
                      ? <ChatAnswer text={m.content} refMap={refMap} onPick={(id) => pickCompany(id)} />
                      : m.content}
                  </div>
                </div>
              ))}
              {chatBusy && (
                <div className="crmiq3-msg crmiq3-msg--assistant">
                  <div className="crmiq3-msg-mark">CE</div>
                  <div className="crmiq3-msg-body crmiq3-typing"><span></span><span></span><span></span></div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
          ) : (
            <div className="crmiq3-surface">
              <button className="crmiq3-back" onClick={() => setSelected(null)}>‹ Back to Raptor</button>
              <CrmCompanyIntelligence companyId={selected} clientId={clientId} layout="centre" initialTab={selectedTab} onSweepComplete={() => { load(); loadFeed(); }} />
            </div>
          )}
        </div>

        <div className="crmiq3-composer-wrap">
          {selected != null && (
            <div className="crmiq3-composer-context">
              Asking about <strong>{companies.find(c => c.id === selected)?.name || 'this company'}</strong>
              <button className="crmiq3-composer-clearctx" onClick={() => setSelected(null)} title="Clear">×</button>
            </div>
          )}
          <div className="crmiq3-composer crmiq3-composer--icon">
            <img className="crm-composer-raptor" src={raptorIcon} alt="Raptor" />
            <textarea
              className="crmiq3-composer-ta"
              value={chatInput}
              onChange={e => setChatInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChat(); } }}
              placeholder={selected != null ? 'Ask about this company\u2026' : 'Ask Raptor\u2026'}
              rows={1}
            />
            <div className="crmiq3-composer-bar">
              <span className="crmiq3-composer-hint">Enter to send · Shift+Enter for newline</span>
              <button className="crmiq3-composer-send" disabled={chatBusy || !chatInput.trim()} onClick={() => sendChat()} title="Send">
                <svg viewBox="0 0 16 16" width="15" height="15"><path d="M8 13V3M8 3L4 7M8 3l4 4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="crmiq3-feed">
        <div className="crmiq3-feed-head">
          <div className="crmiq3-feed-title">Activity</div>
          <div className="crmiq3-feed-filters">
            {FEED_FILTERS.map(f => (
              <button key={f.key} className={'crmiq3-ffilter' + (feedFilter === f.key ? ' crmiq3-ffilter--on' : '')} onClick={() => setFeedFilter(f.key)}>{f.label}</button>
            ))}
          </div>
        </div>
        <div className="crmiq3-feed-list">
          {feedItems.length === 0 ? <p className="crmiq-empty">No activity yet.</p>
            : feedItems.map(item => {
              if (item.feed_type === 'digest') {
                return (
                  <div key={'d' + item.id} className="crmiq3-fcard crmiq3-fcard--digest">
                    <div className="crmiq3-fcard-top"><span className="crmiq-chip" style={{ color: 'var(--crm-violet)', background: 'var(--crm-violet-bg)' }}>Digest</span><span className="crmiq-meta">{relTime(item.feed_date)}</span></div>
                    <div className="crmiq3-fcard-co">{(item.company_names || []).join(', ') || 'Multiple companies'}</div>
                  </div>
                );
              }
              const sc = SIGNAL_COLOURS[item.signal_type] || SIGNAL_COLOURS.news;
              const summaryText = (item.signal_data && (item.signal_data.summary || item.signal_data.title)) || '';
              return (
                <div key={'s' + item.id} className="crmiq3-fcard" onClick={() => item.company_id && pickCompany(item.company_id, 'signals')}>
                  <div className="crmiq3-fcard-top"><span className="crmiq-chip" style={{ color: sc.c, background: sc.bg }}>{sc.l}</span><span className="crmiq-meta">{relTime(item.feed_date)}</span></div>
                  <div className="crmiq3-fcard-co">{item.company_name}</div>
                  {summaryText && <div className="crmiq3-fcard-sum">{summaryText}</div>}
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
}
